import TYPES from '@/types';
import Vue from 'vue';
import { currencyFormat } from '@/vue-app/utils/currency';
import { parseCurrencyToNumber } from '@/vue-app/utils/parse-currency-to-number';

import { v4 } from 'uuid';

// Application
import { MonthlyCollectionDaysService } from '@/modules/my-investment/allianz/recurring-contributions/application/services';

// Domain
import {
  LinkGoalsWealthStateManager,
} from '@/modules/flagship/investor-goal/link-sowos-wealth/domain/state/link-goals-wealth-state-manager';
import { StateManager }
  from '@/modules/my-investment/allianz/recurring-contributions/domain/state/state-manager';
import Inject from '@/modules/shared/domain/di/inject';
import Translator from '@/modules/shared/domain/i18n/translator';
import { Values } from '@/modules/shared/domain/i18n/types';
import {
  MonthlyCollectionDayOptionEntity,
} from '@/modules/my-investment/allianz/allianz-account/domain/entities/monthly-collection-day-option-entity';
import {
  GoalForLinkDto,
} from '@/modules/flagship/investor-goal/link-sowos-wealth/domain/dtos/goal-for-link-dto';
import {
  RecurringContributionEntity,
} from '@/modules/my-investment/allianz/recurring-contributions/domain/entities/recurring-contribution-entity';

type Contribution = {
  collection_day: number;
  amount_field: string;
  total_amount: number;
  new_amount: number;
  total_amount_filed: string;
  show_minimum_amount_error: boolean;
  show_maximum_amount_error: boolean;
}

type Distribution = {
  investor_goal_id: string;
  goal_name: string;
  goal_icon: string;
  recurrent_assigned_amount: number;
  recurrent_assigned_amount_field: string;
  contributions: Array<Contribution>;
}

export default class StepConfigureSecondContributionWealthViewModel {
  @Inject(TYPES.MONTHLY_COLLECTION_DAYS_SERVICE)
  readonly monthly_collection_days_service!: MonthlyCollectionDaysService;

  @Inject(TYPES.ALLIANZ_RECURRING_CONTRIBUTIONS_STATE_MANAGER)
  readonly recurring_contributions_state_manager!: StateManager;

  @Inject(TYPES.LINK_GOALS_WEALTH_STATE_MANAGER)
  readonly link_goals_wealth_state_manager!: LinkGoalsWealthStateManager;

  @Inject(TYPES.I18N)
  readonly translator!: Translator;

  readonly i18n_namespace = 'components.flagship.flagship-link-goals.step-configure-second-contribution-wealth';

  readonly view!: Vue;

  recurring_contributions_state = this.recurring_contributions_state_manager.state;

  link_goals_wealth_state = this.link_goals_wealth_state_manager.state;

  second_reminder_day = '';

  minimum_amount = 500;

  step = 50;

  limit_per_contribution = 50000;

  invalid_distribution = false;

  monthly_collection_days: Array<MonthlyCollectionDayOptionEntity> = [];

  second_monthly_collection_days: Array<MonthlyCollectionDayOptionEntity> = [];

  distributions: Array<Distribution> = [];

  show_define_collection_day = false;

  disabled_first_day = false;

  first_contribution_id = v4();

  second_contribution_id = v4();

  public constructor(view: Vue) {
    this.view = view;
  }

  get currency_minimum_amount() {
    return `${currencyFormat(this.minimum_amount)} MXN`;
  }

  get currency_total_goal_contribution() {
    return `${currencyFormat(this.link_goals_wealth_state.total_goals_contribution)} MXN`;
  }

  get is_disabled() {
    return this.link_goals_wealth_state.first_goal_contribution.collection_day === 0
      || this.link_goals_wealth_state.second_goal_contribution.collection_day === 0
      || this.invalid_distribution;
  }

  translate = (message: string, values?: Values) => this.translator.translate(`${this.i18n_namespace}.${message}`, values);

  loadMonthlyCollectionDays = () => {
    this.monthly_collection_days = this.monthly_collection_days_service.load();
  }

  loadAvailableDaysOnSecondCollectionDay = () => {
    const days = this.monthly_collection_days_service.load();
    this.second_monthly_collection_days = days.filter(
      (day) => this.monthly_collection_days_service
        .showDay(day, this.link_goals_wealth_state.first_goal_contribution.collection_day),
    );
  }

  collectionDayChange = () => {
    this.loadAvailableDaysOnSecondCollectionDay();
  }

  appendDistribution = () => {
    this.distributions.push({
      investor_goal_id: '',
      goal_name: '',
      goal_icon: '',
      recurrent_assigned_amount: 0,
      recurrent_assigned_amount_field: '0',
      contributions: [],
    });
  }

  setDistribution = (
    selected_goal: GoalForLinkDto,
    contributions: Array<RecurringContributionEntity>,
    index: number,
  ) => {
    let first_contribution_amount = 0;
    let second_contribution_amount = 0;
    this.distributions[index].investor_goal_id = selected_goal.investor_goal_id;
    this.distributions[index].goal_name = selected_goal.label;
    this.distributions[index].goal_icon = selected_goal.icon;
    this.distributions[index].recurrent_assigned_amount = selected_goal
      .recurrent_assigned_amount;
    this.distributions[index].recurrent_assigned_amount_field = selected_goal
      .recurrent_assigned_amount_field;
    if (contributions.length > 0) {
      first_contribution_amount = contributions[0].amount;
      this.link_goals_wealth_state.first_goal_contribution.id = contributions[0].id;
      this.link_goals_wealth_state.first_goal_contribution.collection_day = contributions[0]
        .collection_day;
    } else {
      this.link_goals_wealth_state.first_goal_contribution.id = this.first_contribution_id;
    }
    if (contributions.length > 1) {
      second_contribution_amount = contributions[1].amount;
      this.link_goals_wealth_state.second_goal_contribution.id = contributions[1].id;
      this.link_goals_wealth_state.second_goal_contribution.collection_day = contributions[1]
        .collection_day;
    } else {
      this.link_goals_wealth_state.second_goal_contribution.id = this.second_contribution_id;
    }
    this.distributions[index].contributions.push({
      collection_day: this.link_goals_wealth_state.first_goal_contribution.collection_day,
      amount_field: '0',
      total_amount: first_contribution_amount,
      new_amount: 0,
      total_amount_filed: currencyFormat(first_contribution_amount),
      show_minimum_amount_error: false,
      show_maximum_amount_error: false,
    });
    this.distributions[index].contributions.push({
      collection_day: this.link_goals_wealth_state.second_goal_contribution.collection_day,
      amount_field: '0',
      total_amount: second_contribution_amount,
      new_amount: 0,
      total_amount_filed: currencyFormat(second_contribution_amount),
      show_minimum_amount_error: false,
      show_maximum_amount_error: false,
    });
  }

  loadDistributions = () => {
    if (this.distributions.length === 0) {
      this.show_define_collection_day = this.recurring_contributions_state.item.contributions
        .length < 2;
      if (this.recurring_contributions_state.item.contributions.length > 0) {
        this.disabled_first_day = true;
      }
      this.link_goals_wealth_state.selected_goals.forEach((selected_goal, index) => {
        this.appendDistribution();
        this.setDistribution(
          selected_goal,
          this.recurring_contributions_state.item.contributions,
          index,
        );
      });
    }
    this.loadAvailableDaysOnSecondCollectionDay();
  }

  validateTotalAmounts = () => {
    this.invalid_distribution = false;
    // validate if amount per contribution is greater than minimum and letter than maximum
    let total_amount = 0;
    this.distributions.forEach((distribution) => {
      distribution.contributions.forEach((contribution) => {
        const total_contribution = parseCurrencyToNumber(contribution.total_amount_filed);
        total_amount += parseCurrencyToNumber(contribution.amount_field);
        if (!this.show_define_collection_day && (total_contribution < this.minimum_amount
          || total_contribution > this.limit_per_contribution)) {
          this.invalid_distribution = true;
        }
      });
    });
    if (!this.invalid_distribution) {
      this.invalid_distribution = total_amount !== this.link_goals_wealth_state
        .total_goals_contribution && !this.show_define_collection_day;
    }
  }

  updateOtherAmount = (index2: number, index: number) => {
    const pending_amount = this.distributions[index2].recurrent_assigned_amount
      - parseCurrencyToNumber(this.distributions[index2].contributions[index].amount_field);
    const other_index = index === 0 ? 1 : 0;
    if (this.distributions[index2].contributions[other_index].total_amount
      < this.limit_per_contribution
    ) {
      this.distributions[index2].contributions[other_index].amount_field = pending_amount
        .toString();
      this.updateTotalAmount(index2, other_index);
    }
  }

  updateTotalAmount = (index2: number, index: number) => {
    const other_index = index2 === 0 ? 1 : 0;
    let new_total_amount = parseCurrencyToNumber(
      this.distributions[index2].contributions[index].amount_field,
    );
    this.distributions[index2].contributions[index].show_minimum_amount_error = false;
    this.distributions[index2].contributions[index].show_maximum_amount_error = false;
    if (this.distributions.length > other_index) {
      this.distributions[other_index].contributions[index].show_minimum_amount_error = false;
      this.distributions[other_index].contributions[index].show_maximum_amount_error = false;
      new_total_amount += parseCurrencyToNumber(
        this.distributions[other_index].contributions[index].amount_field,
      );
      this.distributions[other_index].contributions[index].total_amount_filed = currencyFormat(
        (this.distributions[other_index].contributions[index].total_amount + new_total_amount),
      );
    }
    this.distributions[index2].contributions[index].total_amount_filed = currencyFormat(
      (this.distributions[index2].contributions[index].total_amount + new_total_amount),
    );
    if (new_total_amount < this.minimum_amount) {
      this.distributions[index2].contributions[index].show_minimum_amount_error = true;
    } else if (new_total_amount > this.limit_per_contribution) {
      this.distributions[index2].contributions[index].show_maximum_amount_error = true;
    }
  }

  decrementAmount = (index2: number, index: number) => {
    const current_amount = parseCurrencyToNumber(
      this.distributions[index2].contributions[index].amount_field,
    );
    const amount = current_amount - this.step;
    const new_amount = this.distributions[index2].contributions[index].total_amount + amount;
    if (amount >= 0) {
      this.distributions[index2].contributions[index].amount_field = amount.toString();
      this.distributions[index2].contributions[index].new_amount = new_amount;
      this.updateTotalAmount(index2, index);
      this.updateOtherAmount(index2, index);
      this.validateTotalAmounts();
    }
  }

  incrementAmount = (index2: number, index: number) => {
    const current_amount = parseCurrencyToNumber(
      this.distributions[index2].contributions[index].amount_field,
    );
    const amount = current_amount + this.step;
    const new_amount = this.distributions[index2].contributions[index].total_amount + amount;
    if (amount > 0 && new_amount <= this.limit_per_contribution
      && amount <= this.link_goals_wealth_state.total_goals_contribution) {
      this.distributions[index2].contributions[index].amount_field = amount.toString();
      this.distributions[index2].contributions[index].new_amount = new_amount;
      this.updateTotalAmount(index2, index);
      this.updateOtherAmount(index2, index);
      this.validateTotalAmounts();
    }
  }

  changeAssignedAmountField = (index2: number, index: number) => {
    const current_amount = this.distributions[index2].contributions[index].total_amount;
    const amount = parseCurrencyToNumber(
      this.distributions[index2].contributions[index].amount_field,
    );
    const new_amount = current_amount + amount;
    if (new_amount >= 0 && new_amount > this.limit_per_contribution) {
      this.distributions[index2].contributions[index].amount_field = '0';
      this.distributions[index2].contributions[index].total_amount_filed = currencyFormat(
        (this.distributions[index2].contributions[index].total_amount),
      );
    } else {
      this.distributions[index2].contributions[index].new_amount = new_amount;
    }
    this.updateTotalAmount(index2, index);
    this.updateOtherAmount(index2, index);
    this.validateTotalAmounts();
  }

  goToPrevStep = () => {
    if (this.recurring_contributions_state.item.contributions.length < 2
      && !this.show_define_collection_day) {
      this.show_define_collection_day = true;
    } else {
      this.view.$emit('prevStep');
    }
  }

  setState = () => {
    this.link_goals_wealth_state.first_goal_contribution.distributions.length = 0;
    this.link_goals_wealth_state.second_goal_contribution.distributions.length = 0;
    this.link_goals_wealth_state.first_goal_contribution.amount = 0;
    this.link_goals_wealth_state.second_goal_contribution.amount = 0;
    this.distributions.forEach((distribution) => {
      distribution.contributions.forEach((contribution, index2) => {
        if (index2 === 0) {
          this.link_goals_wealth_state.first_goal_contribution.distributions.push({
            investor_goal_id: distribution.investor_goal_id,
            recurrent_assigned_amount: parseCurrencyToNumber(contribution.amount_field).toString(),
          });
          this.link_goals_wealth_state.first_goal_contribution.amount += parseCurrencyToNumber(
            contribution.amount_field,
          );
        } else {
          this.link_goals_wealth_state.second_goal_contribution.distributions.push({
            investor_goal_id: distribution.investor_goal_id,
            recurrent_assigned_amount: parseCurrencyToNumber(contribution.amount_field).toString(),
          });
          this.link_goals_wealth_state.second_goal_contribution.amount += parseCurrencyToNumber(
            contribution.amount_field,
          );
        }
      });
    });
  }

  goToNextStep = () => {
    if (this.show_define_collection_day) {
      this.show_define_collection_day = false;
      this.distributions.forEach((distribution, index) => {
        this.distributions[index].contributions[0].collection_day = this.link_goals_wealth_state
          .first_goal_contribution.collection_day;
        this.distributions[index].contributions[1].collection_day = this.link_goals_wealth_state
          .second_goal_contribution.collection_day;
      });
      this.validateTotalAmounts();
    } else {
      this.setState();
      this.view.$emit('linkGoals');
    }
  }

  initialize = () => {
    this.loadMonthlyCollectionDays();
    this.loadDistributions();
    this.validateTotalAmounts();
    this.second_reminder_day = this.translate('recurring_not_defined');
  }
}
